import { useState, type ReactNode } from "react";
import { MarketingFooter, MarketingHeader } from "../public/MarketingChrome";
import { DoorwayBrandIcon } from "../public/DoorwayBrandIcon";

const agentQuickstartPath = "/docs/quickstart-agents";
const humanQuickstartPath = "/docs/quickstart";
const installerCommand = "curl -fsSL --proto '=https' --tlsv1.2 https://meetdoorway.com/install/agents | sh";
const agentPrompt = `Follow the instructions at https://meetdoorway.com/docs/quickstart-agents. Install the Doorway publishing skill if it is not already installed. Publish the current project. Keep secrets, credentials, and access settings out of chat and terminal. Leave visibility and password choices for me to handle in the browser. When publishing is complete, share the live URL.`;

const codexPromptHref = `codex://new?prompt=${encodeURIComponent(agentPrompt)}`;
const claudePromptHref = `claude://claude.ai/new?q=${encodeURIComponent(agentPrompt)}`;

export function AgentsPage() {
  return (
    <div className="app-shell oasis-marketing-shell agents-page">
      <MarketingHeader current="agents" />
      <main>
        <section className="agents-hero">
          <div className="agents-hero-copy">
            <p className="eyebrow"><span className="agents-live-dot" aria-hidden="true" />For Claude Code and Codex</p>
            <h1>The agent that built it can publish it.</h1>
            <p className="hero-intro">Your agent already knows the project. Doorway gives it a safe way to hand you a live link when the work is done.</p>
            <div className="landing-actions">
              <a className="primary-button" href={agentQuickstartPath}>Agent quickstart <span aria-hidden="true">↗</span></a>
              <a className="secondary-button" href={humanQuickstartPath}>Publish it yourself</a>
            </div>
            <div className="agents-install-note">
              <strong>One skill install</strong>
              <span>No separate CLI to learn or authenticate.</span>
            </div>
          </div>
          <AgentHandoffVisual />
        </section>

        <section className="agents-sequence" aria-labelledby="agents-sequence-heading">
          <div className="agents-section-heading">
            <p className="eyebrow">The handoff</p>
            <h2 id="agents-sequence-heading">Your agent does the file work. You stay in control.</h2>
          </div>
          <ol>
            <li><span>1</span><div><h3>Install once</h3><p>Add the Doorway publishing skill. The verified local publisher comes with it.</p></div></li>
            <li><span>2</span><div><h3>Ask naturally</h3><p>Say “publish this with Doorway.” The skill reviews the project and keeps private files out.</p></div></li>
            <li><span>3</span><div><h3>Approve in your browser</h3><p>Sign in, choose the project when needed, and decide who can open the app.</p></div></li>
          </ol>
        </section>

        <section className="agents-capabilities" aria-labelledby="agents-capabilities-heading">
          <div>
            <p className="eyebrow">Available now</p>
            <h2 id="agents-capabilities-heading">A real publish, with human approval at the right moment.</h2>
            <p>Doorway binds your approval to the exact files the agent reviewed. Every successful publish becomes an immutable revision.</p>
          </div>
          <ul>
            <li><span aria-hidden="true">✓</span>Publish supported static sites and eligible source projects</li>
            <li><span aria-hidden="true">✓</span>Leave environment files, Git history, installed packages, and secret-prone files behind</li>
            <li><span aria-hidden="true">✓</span>Choose public or password-protected access in Doorway</li>
            <li><span aria-hidden="true">✓</span>Keep the working version live if a replacement fails</li>
            <li><span aria-hidden="true">✓</span>Return the finished madewithdoorway.com link to the agent</li>
          </ul>
        </section>

        <section className="agents-examples" aria-labelledby="agents-examples-heading">
          <div className="agents-section-heading">
            <p className="eyebrow">What it sounds like</p>
            <h2 id="agents-examples-heading">Finish the build. Ask for the link.</h2>
          </div>
          <div className="agents-example-grid">
            <AgentExample prompt="Publish this project to Doorway." response="I reviewed the project and opened Doorway for your approval. Once you choose access, I’ll finish the publish and return the live link." />
            <AgentExample prompt="The landing page looks good. Go ahead and ship it." response="Doorway recognized the project and prepared a safe copy. Complete the browser approval and I’ll take it from there." />
            <AgentExample prompt="Publish the dashboard and make it password-protected." response="The files are ready. Set the visitor password in Doorway so it stays out of chat and terminal." />
          </div>
        </section>

        <section className="agents-closing" aria-labelledby="agents-closing-heading">
          <DoorwayBrandIcon className="agents-closing-icon" name="door-solid-green" />
          <p>The app is ready.</p>
          <h2 id="agents-closing-heading">Tell your agent to open the Doorway.</h2>
          <PromptLaunchButtons />
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}

export function AgentQuickstartPage() {
  return (
    <DocsQuickstartLayout current="agents">
      <header className="quickstart-hero">
        <p className="eyebrow">Get started</p>
        <h1>Agent quickstart</h1>
        <p className="hero-intro">Let Claude Code or Codex publish the project you already have open. The agent handles file review and packaging. You handle sign-in and access in Doorway.</p>
        <p className="quickstart-alternative">Prefer to publish from your browser? <a href={humanQuickstartPath}>Follow the human quickstart.</a></p>
      </header>

      <section className="quickstart-section" aria-labelledby="agent-install-heading">
        <span className="quickstart-number">01</span>
        <div>
          <h2 id="agent-install-heading">Install the Doorway skill once</h2>
          <p>This adds the verified local publisher to Claude Code and Codex. There is no separate CLI login or command set to learn.</p>
          <CodeBlock code={installerCommand} label="Doorway agent skill installer" />
          <p className="quickstart-note">The installer supports macOS, Linux, and Windows through WSL. Run it again whenever you want the latest skill.</p>
        </div>
      </section>

      <section className="quickstart-section" aria-labelledby="agent-prompt-heading">
        <span className="quickstart-number">02</span>
        <div>
          <h2 id="agent-prompt-heading">Prompt your agent</h2>
          <p>Open the project in Claude Code or Codex, then use one of these buttons or copy the prompt below.</p>
          <PromptLaunchButtons />
          <CodeBlock code={agentPrompt} label="Doorway publishing prompt" multiline />
        </div>
      </section>

      <section className="quickstart-section" aria-labelledby="agent-journey-heading">
        <span className="quickstart-number">03</span>
        <div>
          <h2 id="agent-journey-heading">Complete the browser handoff</h2>
          <ol className="quickstart-steps">
            <li>The skill checks the project and omits local-only and secret-prone files.</li>
            <li>Doorway opens in your browser. Sign in if needed.</li>
            <li>Choose the destination project when Doorway asks, then choose public or password-protected access.</li>
            <li>Doorway publishes the exact reviewed files as an immutable revision.</li>
            <li>Your agent receives the live URL and reports it back.</li>
          </ol>
          <aside className="quickstart-safety"><strong>Keep access choices in the browser.</strong><span>Passwords, browser cookies, credentials, and approval secrets never belong in chat or terminal.</span></aside>
        </div>
      </section>

      <Troubleshooting />
    </DocsQuickstartLayout>
  );
}

export function HumanQuickstartPage() {
  return (
    <DocsQuickstartLayout current="human">
      <header className="quickstart-hero">
        <p className="eyebrow">Get started</p>
        <h1>Publish your first app</h1>
        <p className="hero-intro">Choose the project folder on your computer, review the safe copy, and share the live link. You can do the whole thing in your browser.</p>
        <p className="quickstart-alternative">Using Claude Code or Codex? <a href={agentQuickstartPath}>Follow the agent quickstart.</a></p>
      </header>

      <section className="human-quickstart-grid" aria-label="How to publish with Doorway">
        <QuickstartCard number="01" title="Choose your project folder">Open <a href="/">meetdoorway.com</a> and use the folder picker to choose the local project you want to share.</QuickstartCard>
        <QuickstartCard number="02" title="Review the safe copy">Doorway shows what it will include and leaves environment files, Git history, installed packages, and secret-prone files on your computer.</QuickstartCard>
        <QuickstartCard number="03" title="Sign in">Continue with Google to open your Doorway workspace. Your reviewed files wait in a one-use browser handoff.</QuickstartCard>
        <QuickstartCard number="04" title="Choose where it lives and who gets in">Create or choose the destination project, then set public or password-protected access.</QuickstartCard>
        <QuickstartCard number="05" title="Publish and share">Doorway tests the new revision, activates it, and gives you one madewithdoorway.com link to share.</QuickstartCard>
      </section>

      <section className="quickstart-support" aria-labelledby="supported-projects-heading">
        <div>
          <p className="eyebrow">Supported projects</p>
          <h2 id="supported-projects-heading">The folder picker checks before anything goes live.</h2>
        </div>
        <p>Doorway accepts finished output from plain HTML, Vite, Create React App, Astro, Next static export, Parcel, SvelteKit, React Router SPA, Nuxt generate, Gatsby, Hugo, Docusaurus, and Eleventy. Eligible paid workspaces can also publish supported source projects.</p>
      </section>

      <section className="quickstart-recovery">
        <strong>If an update fails, the working version stays live.</strong>
        <span>Fix the issue and publish again when you are ready.</span>
      </section>

      <section className="quickstart-final">
        <DoorwayBrandIcon className="quickstart-final-icon" name="door-solid-green" />
        <h2>Start with the folder you already have.</h2>
        <a className="primary-button" href="/#publish">Choose your project <span aria-hidden="true">↗</span></a>
      </section>
    </DocsQuickstartLayout>
  );
}

function DocsQuickstartLayout({ current, children }: { current: "agents" | "human"; children: ReactNode }) {
  return (
    <div className="app-shell oasis-marketing-shell quickstart-page">
      <MarketingHeader current="docs" />
      <main className="quickstart-main">
        <nav className="quickstart-tabs" aria-label="Quickstart guides">
          <a href={humanQuickstartPath} aria-current={current === "human" ? "page" : undefined}>Human quickstart</a>
          <a href={agentQuickstartPath} aria-current={current === "agents" ? "page" : undefined}>Agent quickstart</a>
        </nav>
        {children}
      </main>
      <MarketingFooter />
    </div>
  );
}

function AgentHandoffVisual() {
  return (
    <div className="agents-handoff" aria-label="An agent hands a reviewed project to Doorway for browser approval, then receives the live link">
      <div className="agents-handoff-bar"><i /><i /><i /><span>Claude Code · project</span></div>
      <div className="agents-chat-row agents-chat-user"><span>You</span><p>Publish this with Doorway.</p></div>
      <div className="agents-chat-row agents-chat-agent">
        <span>Agent</span>
        <div><p>Project checked. Private files stay local.</p><small><i /> Waiting for your browser approval</small></div>
      </div>
      <div className="agents-threshold" aria-hidden="true"><span /><DoorwayBrandIcon name="door-solid-green" /><span /></div>
      <div className="agents-browser-card">
        <div><strong>Files reviewed and ready</strong><small>You choose who can open this app.</small></div>
        <button type="button" tabIndex={-1}>Approve and publish</button>
      </div>
      <div className="agents-live-link"><span aria-hidden="true">✓</span><div><small>LIVE</small><strong>launch-brief.madewithdoorway.com</strong></div></div>
    </div>
  );
}

function AgentExample({ prompt, response }: { prompt: string; response: string }) {
  return (
    <article className="agents-example">
      <div className="agents-example-prompt"><span>You</span><p>{prompt}</p></div>
      <div className="agents-example-response"><DoorwayBrandIcon name="door-outline-green" /><p>{response}</p></div>
    </article>
  );
}

function PromptLaunchButtons() {
  return (
    <div className="agent-prompt-buttons" aria-label="Open the Doorway publishing prompt">
      <a className="primary-button agent-launch-button" href={codexPromptHref}><span className="agent-client-mark" aria-hidden="true">⌘</span>Prompt Codex</a>
      <a className="secondary-button agent-launch-button" href={claudePromptHref}><span className="agent-client-mark" aria-hidden="true">C</span>Prompt Claude</a>
    </div>
  );
}

function CodeBlock({ code, label, multiline = false }: { code: string; label: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={`quickstart-code${multiline ? " quickstart-code-multiline" : ""}`}>
      <div><span>{label}</span><button type="button" onClick={() => {
        void navigator.clipboard.writeText(code).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1800);
        });
      }}>{copied ? "Copied" : "Copy"}</button></div>
      <pre><code>{code}</code></pre>
    </div>
  );
}

function QuickstartCard({ number, title, children }: { number: string; title: string; children: ReactNode }) {
  return <article><span>{number}</span><h2>{title}</h2><p>{children}</p></article>;
}

function Troubleshooting() {
  return (
    <section className="quickstart-troubleshooting" aria-labelledby="troubleshooting-heading">
      <p className="eyebrow">Troubleshooting</p>
      <h2 id="troubleshooting-heading">Three common recovery paths.</h2>
      <details><summary>Doorway does not recognize the project</summary><p>Use finished output from a supported framework, or check whether your paid workspace can use a supported source build. The skill will name the unsupported project boundary without uploading it.</p></details>
      <details><summary>The browser approval expired</summary><p>Ask your agent to publish again. Doorway creates a fresh browser handoff and discards the expired approval.</p></details>
      <details><summary>A replacement failed</summary><p>Your working version stays online. Fix the issue the agent reports, then run the publish request again.</p></details>
    </section>
  );
}
